import { LANDING_PAGE_DEMO_PATH_STOPS } from "@repo/ui/components/shadcn/hexagon-background";
import {
	getLandingDemoTopicCardImageSrcs,
	LANDING_DEMO_CARD_IMAGE_ROOT,
} from "./landing-demo-seed";

/** Matches `solution-demo/index.tsx` and hexagon marketing paths. */
export const SOLUTION_DEMO_LANDING_IMG = "/images/landing-page";

const SOLUTION_DEMO_PLAYER_SPRITE_SUFFIXES = [
	"tr",
	"r",
	"br",
	"tl",
	"l",
	"bl",
] as const;

const SOLUTION_DEMO_SCENERY_IMAGES = [
	`${SOLUTION_DEMO_LANDING_IMG}/cactus.png`,
	`${SOLUTION_DEMO_LANDING_IMG}/tree.png`,
	`${SOLUTION_DEMO_LANDING_IMG}/moon-rock.png`,
] as const;

const SOLUTION_DEMO_BIOME_BACKGROUNDS = [
	`${SOLUTION_DEMO_LANDING_IMG}/screenshots/base-desert.jpg`,
	`${SOLUTION_DEMO_LANDING_IMG}/screenshots/base-grass.jpg`,
	`${SOLUTION_DEMO_LANDING_IMG}/screenshots/base-moon.jpg`,
] as const;

/** Loaded via `next/image` priority on the solution section — not `Image()` preloaded. */
export const SOLUTION_DEMO_BACKGROUND_IMAGE_SRC = `${SOLUTION_DEMO_LANDING_IMG}/background-solution.jpg`;

const SOLUTION_DEMO_CRITICAL_CHROME_IMAGES = [
	`${SOLUTION_DEMO_LANDING_IMG}/hextile.png`,
] as const;

const SOLUTION_DEMO_DEFERRED_CHROME_IMAGES = [
	`${SOLUTION_DEMO_LANDING_IMG}/castle.png`,
] as const;

/** Completion overlay + marketing demo topic success / level-up slides. */
const SOLUTION_DEMO_COMPLETION_OVERLAY_IMAGES = [
	"/images/confetti-trophy.png",
	"/images/confetti-rocket-up.png",
	"/images/confetti-skull.png",
	"/images/player-celebrate.png",
	"/images/wings.png",
	"/images/shine-stars.png",
	"/images/shadow-flat.png",
	"/images/wallet/gold-coins.png",
] as const;

const CREATE_PILLAR_CARD_IMAGE_PATH_PREFIX = `${LANDING_DEMO_CARD_IMAGE_ROOT}/f0-`;

function addPathStopIconSrc(
	imageSrcs: Set<string>,
	icon: string | { src?: string },
): void {
	if (typeof icon === "string") {
		const trimmed = icon.trim();
		if (trimmed) {
			imageSrcs.add(trimmed);
		}
		return;
	}
	const src = icon.src?.trim();
	if (src) {
		imageSrcs.add(src);
	}
}

function collectHexMapCriticalImageSrcs(imageSrcs: Set<string>): void {
	for (const stop of LANDING_PAGE_DEMO_PATH_STOPS) {
		addPathStopIconSrc(imageSrcs, stop.icon);
	}

	for (const suffix of SOLUTION_DEMO_PLAYER_SPRITE_SUFFIXES) {
		imageSrcs.add(`${SOLUTION_DEMO_LANDING_IMG}/player-${suffix}.png`);
	}

	for (const src of SOLUTION_DEMO_SCENERY_IMAGES) {
		imageSrcs.add(src);
	}
	for (const src of SOLUTION_DEMO_BIOME_BACKGROUNDS) {
		imageSrcs.add(src);
	}
	for (const src of SOLUTION_DEMO_CRITICAL_CHROME_IMAGES) {
		imageSrcs.add(src);
	}
}

function collectCreatePillarCardImageSrcs(imageSrcs: Set<string>): void {
	for (const src of getLandingDemoTopicCardImageSrcs()) {
		if (src.startsWith(CREATE_PILLAR_CARD_IMAGE_PATH_PREFIX)) {
			imageSrcs.add(src);
		}
	}
}

/**
 * Hex map + default (Create) pillar card art — enough to unlock the solution demo shell.
 * `background-solution.jpg` is handled by `next/image` on the section.
 */
export function getSolutionDemoCriticalPreloadImageSrcs(): readonly string[] {
	const imageSrcs = new Set<string>(["/images/swipe.png"]);
	collectHexMapCriticalImageSrcs(imageSrcs);
	collectCreatePillarCardImageSrcs(imageSrcs);
	return [...imageSrcs];
}

/** Remaining cards, castle, and completion overlay art — warmed after critical tier. */
export function getSolutionDemoDeferredPreloadImageSrcs(): readonly string[] {
	const critical = new Set(getSolutionDemoCriticalPreloadImageSrcs());
	const deferred = new Set<string>();

	for (const src of getLandingDemoTopicCardImageSrcs()) {
		if (!critical.has(src)) {
			deferred.add(src);
		}
	}
	for (const src of SOLUTION_DEMO_DEFERRED_CHROME_IMAGES) {
		deferred.add(src);
	}
	for (const src of SOLUTION_DEMO_COMPLETION_OVERLAY_IMAGES) {
		deferred.add(src);
	}

	return [...deferred];
}

/**
 * All static image URLs for the landing solution demo (critical ∪ deferred).
 */
export function getSolutionDemoPreloadImageSrcs(): readonly string[] {
	const imageSrcs = new Set<string>(
		getSolutionDemoCriticalPreloadImageSrcs(),
	);
	for (const src of getSolutionDemoDeferredPreloadImageSrcs()) {
		imageSrcs.add(src);
	}
	return [...imageSrcs];
}
